import { InternalCanvas } from './canvas';
import { SerializedCanvas, SerializedObject } from './types';

export function isActiveSelection(
    obj: fabric.Object,
): obj is fabric.ActiveSelection {
    return obj.isType('activeSelection');
}

export function isTextbox(obj: fabric.Object): obj is fabric.Textbox {
    return obj.isType('textbox');
}

export function isImage(
    obj: fabric.Object,
): obj is fabric.Image & { imagetype: string } {
    return obj.isType('image');
}

export function isMap(obj: fabric.Object): boolean {
    return isImage(obj) && obj.imagetype === 'map-overhead';
}

// https://stackoverflow.com/a/2117523
export function uuidv4(): string {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
        (c ^ (Math.floor(Math.random() * 16) & (15 >> (c / 4)))).toString(16),
    );
}

export function normalizeObjectUUID(
    uuidOrObject: string | fabric.Object,
): string {
    return typeof uuidOrObject === 'string' ? uuidOrObject : uuidOrObject.uuid;
}

export function cloneObject<T extends fabric.Object = fabric.Object>(
    obj: T,
): Promise<T> {
    return new Promise((resolve) => obj.clone(resolve, fabricExtras));
}

export function cloneObjects<T extends fabric.Object = fabric.Object>(
    objs: T[],
): Promise<T[]> {
    return Promise.all(objs.map(cloneObject));
}

async function restoreGroupObjectsState<T extends fabric.Group = fabric.Group>(
    group: T,
    clone = false,
): Promise<T> {
    if (clone) {
        group = await cloneObject(group);
    }

    group._restoreObjectsState();

    return group;
}

export async function flattenObjects(
    objs: fabric.Object | fabric.Object[],
    clone = false,
): Promise<fabric.Object[]> {
    return Promise.all(
        (Array.isArray(objs) ? objs : [objs]).map(
            async (obj: fabric.Object) => {
                if (!isActiveSelection(obj)) {
                    return [obj];
                }

                return flattenObjects(
                    (await restoreGroupObjectsState(obj, clone))._objects,
                );
            },
        ),
    ).then((objs) => objs.flat());
}

const fabricExtras = ['uuid', 'imagetype', 'imagekey'];

export function serializeObject(obj: fabric.Object): SerializedObject {
    return obj.toJSON(fabricExtras);
}

export function serializeCanvas(canvas: InternalCanvas): SerializedCanvas {
    return canvas._fabricCanvas.toJSON(fabricExtras);
}
